import React, { lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { getAuthValue, initializeAuthSession } from "./lib/authSession";
import { BASE_INICIO, BASE_PROFESIONAL, LEGACY_INICIO_REDIRECTS } from "./app/routes/routeConstants";
import {
    PageLoader,
    renderLazy,
    RutaProtegidaGestionFamiliar,
    RutaProtegidaPaciente,
} from "./app/routes/routeGuards";
import { LOGIN_INTERNAL_PATH, LOGIN_PATIENT_PATH, START_PATH } from "./config/runtime";

import './custom.scss';
import './index.css';

import RutaProtegida from "./components/login/RutaProtegida";
import Header from "./components/header/Header";
import Footer from "./components/footer/Footer";
import InicioPaciente from "./components/perfil_paciente/InicioPaciente";
import ScrollToTop from "./ScrollToTop";

const Login = lazy(() => import("./components/login/Login"));
const Registro = lazy(() => import("./components/login/Registro"));
const Inicio = lazy(() => import("./components/inicio/Inicio"));
const InicioAdmin = lazy(() => import("./components/inicio_admin/Inicio_admin"));
const DashboardGestor = lazy(() => import("./components/gestor_consultorios/DashboardGestor"));
const DashboardProfesional = lazy(() => import("./components/perfil_profesional/DashboardProfesional"));
const ConsultoriosGestor = lazy(() => import("./components/gestor_consultorios/ConsultoriosGestor"));
const SolicitudesAlquilerConsultorio = lazy(() => import("./components/gestor_consultorios/SolicitudesAlquilerConsultorio"));
const HorariosTiposAlquiler = lazy(() => import("./components/gestor_consultorios/HorariosTiposAlquiler"));
const Historial_turnos = lazy(() => import("./components/historial_turnos_cli/Historial"));
const AccountInfo = lazy(() => import("./components/cliente_configuracion/AccountInfo"));
const Servicio = lazy(() => import("./components/Servicio/Servicio"));
const ReservaContext = lazy(() => import("./components/reserva/reservaContext"));
const AgendaConsultoriosAdmin = lazy(() => import("./components/reserva_admin/AgendaConsultoriosAdmin"));
const ConfiguracionAdmin = lazy(() => import("./components/configuracion_admin/ConfiguracionAdmin"));
const Perfil = lazy(() => import("./components/perfil/Perfil"));
const AgendaYAlquilerConsultorioProfesional = lazy(() => import("./components/perfil_profesional/AgendaYAlquilerConsultorioProfesional"));
const MisPacientesProfesional = lazy(() => import("./components/perfil_profesional/MisPacientesProfesional"));
const HistoriaClinicaPaciente = lazy(() => import("./components/perfil_profesional/HistoriaClinicaPaciente"));
const PreciosDuracionServicios = lazy(() => import("./components/perfil_profesional/PreciosDuracionServicios"));
const AdmisionPacientesUnificado = lazy(() => import("./components/perfil_profesional/AdmisionPacientesUnificado"));
const NotificacionesProfesional = lazy(() => import("./components/perfil_profesional/NotificacionesProfesional"));
const ResetPassword = lazy(() => import("./components/cliente_configuracion/ResetPassword"));
const ActivarCuenta = lazy(() => import("./components/login/ActivarCuenta"));
const SolicitudesUnificadas = lazy(() => import("./components/gestion_responsables/SolicitudesUnificadas"));
const PersonasUnificadas = lazy(() => import("./components/gestion_responsables/PersonasUnificadas"));
const SolicitudProfesionalForm = lazy(() => import("./components/login/SolicitudProfesionalForm"));
const ResumenApp = lazy(() => import("./components/inicio/ResumenApp"));
const DashboardPaciente = lazy(() => import("./components/perfil_paciente/DashboardPaciente"));
const NotificacionesPaciente = lazy(() => import("./components/perfil_paciente/NotificacionesPaciente"));
const AreaLudica = lazy(() => import("./components/inicio/AreaLudica"));
const Experiencias = lazy(() => import("./components/inicio/Experiencias"));
const AreaTerapeuticaEducativa = lazy(() => import("./components/inicio/AreaTerapeuticaEducativa"));
const SobreNosotrosPublico = lazy(() => import("./components/inicio/SobreNosotrosPublico"));
const HubFamiliarPage = lazy(() => import("./features/responsables/pages").then((module) => ({ default: module.HubFamiliarPage })));
const SolicitarPacientePage = lazy(() => import("./features/responsables/pages").then((module) => ({ default: module.SolicitarPacientePage })));
const SolicitarVinculacionPage = lazy(() => import("./features/responsables/pages").then((module) => ({ default: module.SolicitarVinculacionPage })));

function App() {
    const [authLoading, setAuthLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        initializeAuthSession().finally(() => {
            if (mounted) setAuthLoading(false);
        });
        return () => { mounted = false; };
    }, []);

    return (
        <div className="App">
            <ScrollToTop></ScrollToTop>
            <Routes>

                {/* Layout paciente: cuando paciente/responsable está logueado */}
                <Route path={BASE_INICIO} element={<InicioLayout authLoading={authLoading} />}>
                    <Route index element={<InicioIndex authLoading={authLoading} />} />
                    <Route path="conocenos" element={renderLazy(ResumenApp, {}, "Cargando contenido...")} />
                    <Route path="area-terapeutica-educativa" element={renderLazy(AreaTerapeuticaEducativa, {}, "Cargando área terapéutica...")} />
                    <Route path="area-ludica" element={renderLazy(AreaLudica, {}, "Cargando área lúdica...")} />
                    <Route path="experiencias" element={renderLazy(Experiencias, {}, "Cargando experiencias...")} />
                    <Route path="sobre-nosotros" element={renderLazy(SobreNosotrosPublico, {}, "Cargando contenido...")} />
                    <Route path="realizar_reserva" element={<RutaProtegidaPaciente authLoading={authLoading}>{renderLazy(ReservaContext, {}, "Cargando reserva...")}</RutaProtegidaPaciente>} />
                    <Route path="configuracion_cuenta" element={<RutaProtegidaPaciente authLoading={authLoading}>{renderLazy(AccountInfo, {}, "Cargando cuenta...")}</RutaProtegidaPaciente>} />
                    <Route path="mis_reservas" element={<RutaProtegidaPaciente authLoading={authLoading}>{renderLazy(Historial_turnos, {}, "Cargando reservas...")}</RutaProtegidaPaciente>} />
                    <Route path="notificaciones" element={<RutaProtegidaPaciente authLoading={authLoading}>{renderLazy(NotificacionesPaciente, {}, "Cargando notificaciones...")}</RutaProtegidaPaciente>} />
                    <Route path="solicitar_vinculacion" element={<RutaProtegidaGestionFamiliar authLoading={authLoading}>{renderLazy(SolicitarVinculacionPage, {}, "Cargando solicitud...")}</RutaProtegidaGestionFamiliar>} />
                    <Route path="solicitar_paciente" element={<RutaProtegidaPaciente authLoading={authLoading}>{renderLazy(SolicitarPacientePage, {}, "Cargando solicitud...")}</RutaProtegidaPaciente>} />
                    <Route path="mis_pacientes_vinculados" element={<Navigate to="/inicio/hub_familiar" replace />} />
                    <Route path="hub_familiar" element={<RutaProtegidaGestionFamiliar authLoading={authLoading}>{renderLazy(HubFamiliarPage, {}, "Cargando hub familiar...")}</RutaProtegidaGestionFamiliar>} />
                    {LEGACY_INICIO_REDIRECTS.map(({ from, to }) => (
                        <Route key={from} path={from} element={<Navigate to={`${BASE_INICIO}/${to}`} replace />} />
                    ))}
                </Route>

                {/* Ruta para el profesional: /profesional/* */}
                <Route path="/profesional/*" element={<RutaProtegida authLoading={authLoading} fallback={<PageLoader message="Cargando panel..." />}><InicioProfesional /></RutaProtegida>} />

                {/* Ruta para el gestor de consultorios */}
                <Route path="/gestor_consultorio/*" element={<RutaProtegida authLoading={authLoading} fallback={<PageLoader message="Cargando panel..." />}><InicioGestorWrapper /></RutaProtegida>} />

                {/* Ruta para el admin con rutas anidadas */}
                <Route path="/inicio_admin/*" element={<RutaProtegida authLoading={authLoading} fallback={<PageLoader message="Cargando panel..." />}>{renderLazy(InicioAdmin, {}, "Cargando panel...")}</RutaProtegida>}>
                    <Route index element={<InicioAdminHome />} />
                    <Route path="servicios" element={renderLazy(Servicio, {}, "Cargando servicios...")} />
                    <Route path="personas" element={renderLazy(PersonasUnificadas, {}, "Cargando personas...")} />
                    <Route path="pacientes" element={<Navigate to="/inicio_admin/personas" replace />} />
                    <Route path="profesionales" element={<Navigate to="/inicio_admin/personas" replace />} />
                    <Route path="responsables" element={<Navigate to="/inicio_admin/personas" replace />} />
                    <Route path="gestores" element={<Navigate to="/inicio_admin/personas" replace />} />
                    <Route path="solicitudes" element={renderLazy(SolicitudesUnificadas, {}, "Cargando solicitudes...")} />
                    <Route path="solicitudes_responsables" element={<Navigate to="/inicio_admin/solicitudes" replace />} />
                    <Route path="solicitudes_pacientes_profesionales" element={<Navigate to="/inicio_admin/solicitudes" replace />} />
                    <Route path="solicitudes_profesionales" element={<Navigate to="/inicio_admin/solicitudes" replace />} />
                    <Route path="consultorios" element={renderLazy(ConsultoriosGestor, {}, "Cargando consultorios...")} />
                    <Route path="consultorios/solicitudes-alquiler" element={renderLazy(SolicitudesAlquilerConsultorio, {}, "Cargando solicitudes...")} />
                    <Route path="horarios_tipos_alquiler" element={renderLazy(HorariosTiposAlquiler, {}, "Cargando horarios...")} />
                    <Route path="agenda" element={renderLazy(AgendaConsultoriosAdmin, {}, "Cargando agenda...")} />
                    <Route path="configuracion" element={renderLazy(ConfiguracionAdmin, {}, "Cargando configuración...")}></Route>
                    <Route path="agenda_consultorios_profesional" element={renderLazy(AgendaYAlquilerConsultorioProfesional, {}, "Cargando agenda...")} />
                    <Route path="horarios_uso_servicio" element={<Navigate to="/inicio_admin/admision_pacientes" replace />} />
                    <Route path="precios_duracion" element={renderLazy(PreciosDuracionServicios, {}, "Cargando configuración...")} />
                    <Route path="mis_pacientes" element={renderLazy(MisPacientesProfesional, {}, "Cargando pacientes...")} />
                    <Route path="historia-clinica/:id_paciente" element={renderLazy(HistoriaClinicaPaciente, {}, "Cargando historia clínica...")} />
                    <Route path="admision_pacientes" element={renderLazy(AdmisionPacientesUnificado, {}, "Cargando admisión...")} />
                    <Route path="notificaciones" element={renderLazy(NotificacionesProfesional, {}, "Cargando notificaciones...")} />
                    <Route path="mensajes" element={<Navigate to="/inicio_admin" replace state={{ openMensajes: true }} />} />
                    <Route path="reservar_turno" element={renderLazy(ReservaContext, { esDeProfesional: true }, "Cargando reserva...")} />
                    <Route path="realizar_reserva" element={renderLazy(ReservaContext, { esDeAdmin: true }, "Cargando reserva...")} />
                    <Route path="perfil" element={renderLazy(Perfil, {}, "Cargando perfil...")}></Route>
                </Route>

                {/* Rutas básicas */}
                <Route path="/" element={<Navigate to={START_PATH} />} />
                <Route path="*" element={<Navigate to={START_PATH} />} />
                <Route path="/login" element={<Navigate to={LOGIN_PATIENT_PATH} replace />} />
                <Route path={LOGIN_PATIENT_PATH} element={renderLazy(Login, { tipo: "paciente" }, "Cargando acceso...")} />
                <Route path="/login/profesional" element={renderLazy(Login, { tipo: "profesional" }, "Cargando acceso...")} />
                <Route path={LOGIN_INTERNAL_PATH} element={renderLazy(Login, { tipo: "interno" }, "Cargando acceso...")} />
                <Route path="/acceso-colaboradores" element={renderLazy(Login, { tipo: "interno" }, "Cargando acceso...")} />
                <Route path="/forgot-password" element={<Navigate to={`${LOGIN_PATIENT_PATH}?forgot=1`} replace />}></Route>
                <Route path="/reset-password/:token" element={renderLazy(ResetPassword, {}, "Cargando formulario...")}></Route>
                <Route path="/activar-cuenta" element={renderLazy(ActivarCuenta, {}, "Cargando activación...")}></Route>
                <Route path="/registro" element={renderLazy(Registro, {}, "Cargando registro...")}></Route>
                <Route path="/solicitud-profesional" element={renderLazy(SolicitudProfesionalForm, {}, "Cargando formulario...")} />
            </Routes>
        </div>
    );
}

/** Home de /inicio: si es paciente o responsable muestra DashboardPaciente; si no, la landing Inicio */
function InicioIndex({ authLoading }) {
    if (authLoading) return renderLazy(Inicio, {}, "Cargando inicio...");
    const userRol = getAuthValue('userRol');
    const esPacienteOuResponsable = (userRol === 'paciente' || userRol === 'responsable');
    return esPacienteOuResponsable
        ? renderLazy(DashboardPaciente, {}, "Cargando panel...")
        : renderLazy(Inicio, {}, "Cargando inicio...");
}

/** Layout profesional: solo profesionales; redirige al resto a su dashboard */
function InicioProfesional() {
    const userRol = getAuthValue('userRol');
    if (userRol !== 'profesional') return <Navigate to={userRol === 'gestor' ? '/gestor_consultorio' : '/inicio_admin'} replace />;
    return renderLazy(DashboardProfesional, {}, "Cargando panel...");
}

/** Solo gestor: redirige admin/profesional a su dashboard */
function InicioGestorWrapper() {
    const userRol = getAuthValue('userRol');
    if (userRol === 'admin') return <Navigate to="/inicio_admin" replace />;
    if (userRol === 'profesional') return <Navigate to={BASE_PROFESIONAL} replace />;
    if (userRol !== 'gestor') return <Navigate to="/inicio" replace />;
    return renderLazy(DashboardGestor, {}, "Cargando panel...");
}

/** Home de inicio_admin: admin va a agenda; profesionales → /profesional; gestor → /gestor_consultorio */
function InicioAdminHome() {
    const userRol = getAuthValue('userRol');
    if (userRol === 'admin') return <Navigate to="/inicio_admin/agenda" replace />;
    if (userRol === 'profesional') return <Navigate to={BASE_PROFESIONAL} replace />;
    if (userRol === 'gestor') return <Navigate to="/gestor_consultorio" replace />;
    return <Navigate to="/inicio_admin/configuracion" replace />;
}

/** Layout según tipo de usuario: paciente/responsable → dashboard con sidebar; invitado → landing pública */
const InicioLayout = ({ authLoading }) => {
    const isAuthenticated = !!getAuthValue("auth");
    const userRol = getAuthValue("userRol");
    const location = useLocation();
    const esPacienteOuResponsable = (isAuthenticated && (userRol === 'paciente' || userRol === 'responsable'));
    const esStaff = (isAuthenticated && ['admin', 'gestor', 'profesional'].includes(userRol || ''));

    if (authLoading) {
        return <Outlet />;
    }

    // Admin, gestor o profesional no deben acceder a la página pública: redirigir al dashboard
    if (esStaff) {
        const userRol = getAuthValue('userRol');
        if (userRol === 'profesional') return <Navigate to={BASE_PROFESIONAL} replace />;
        if (userRol === 'gestor') return <Navigate to="/gestor_consultorio" replace />;
        return <Navigate to="/inicio_admin" replace />;
    }

    if (esPacienteOuResponsable) {
        return (
            <InicioPaciente>
                <Outlet />
            </InicioPaciente>
        );
    }

    // Las rutas públicas bajo /inicio renderizan su propio diseño (sin header/footer antiguos).
    if (location.pathname.startsWith('/inicio')) {
        return <Outlet />;
    }

    return (
        <>
            <Header />
            <Outlet />
            <Footer />
        </>
    );
};

export default App;
